import React from "react";
import { Link } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="text-xl sm:text-2xl font-bold text-[#0B2B62]">{title}</h2>
    <div className="mt-3 space-y-3 text-slate-700 leading-relaxed text-[15px]">{children}</div>
  </section>
);

export default function TermosUso() {
  usePageTitle(
    "Termos de Uso",
    "Termos e condições de uso da plataforma RAF Direct: natureza do serviço (orientação, não tratamento), responsabilidades e uso adequado."
  );
  return (
    <div className="bg-white" data-testid="terms-of-use-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Documento legal</div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0B2B62]">Termos de Uso</h1>
        <p className="mt-3 text-sm text-slate-500">
          Última atualização: 05 de fevereiro de 2026
        </p>

        <Section title="1. Aceitação dos termos">
          <p>
            Ao acessar ou utilizar a plataforma <strong>RAF Direct</strong>
            {" "}(<a href="https://rafdirect.com.br" className="text-[#0B2B62] underline">rafdirect.com.br</a>),
            você declara ter lido, compreendido e aceito integralmente estes Termos de Uso e a nossa
            {" "}<Link to="/politica-de-privacidade" className="text-[#0B2B62] underline">Política de Privacidade</Link>.
            Se não concordar, por favor não utilize o serviço.
          </p>
        </Section>

        <Section title="2. Natureza do serviço">
          <p>
            A RAF Direct é uma <strong>plataforma digital de orientação, informação e
            encaminhamento</strong>. <strong>Não prestamos serviços médicos, psicológicos, psiquiátricos,
            de diagnóstico ou de tratamento</strong>. Os serviços clínicos são prestados por clínicas e
            profissionais credenciados, sob a responsabilidade técnica exclusiva destes, respeitando
            os respectivos conselhos profissionais (CRM, CRP, COREN, entre outros).
          </p>
          <p>
            Emergências médicas devem ser tratadas nos serviços públicos e privados de urgência
            (SAMU 192, Corpo de Bombeiros 193, pronto-socorros).
          </p>
        </Section>

        <Section title="3. Cadastro e perfis">
          <p>Os perfis disponíveis são:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Família / paciente</strong> — busca orientação e encaminhamento.</li>
            <li><strong>Clínica</strong> — unidade credenciada que recebe encaminhamentos.</li>
            <li><strong>Profissional</strong> — médico, psicólogo ou outro profissional credenciado.</li>
            <li><strong>Consultor</strong> — parceiro que faz a análise e orientação inicial.</li>
            <li><strong>Administrador</strong> — equipe interna da RAF Direct.</li>
          </ul>
          <p>
            É de sua responsabilidade fornecer informações verdadeiras, manter suas credenciais em
            sigilo e comunicar imediatamente qualquer uso indevido de sua conta.
          </p>
        </Section>

        <Section title="4. Uso adequado">
          <p>Ao utilizar a plataforma, você concorda em NÃO:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Fornecer dados falsos, enganosos ou de terceiros sem autorização;</li>
            <li>Utilizar a plataforma para fins ilícitos, discriminatórios ou contrários à ética;</li>
            <li>Tentar acessar áreas restritas, interferir na segurança ou realizar engenharia reversa;</li>
            <li>Reproduzir conteúdos da plataforma sem autorização.</li>
          </ul>
        </Section>

        <Section title="5. Pagamentos e planos">
          <p>
            O atendimento inicial e a orientação para famílias são gratuitos. Serviços prestados por
            clínicas e profissionais credenciados podem ter custos, sempre informados com transparência
            antes da contratação. Pagamentos são processados por gateways certificados (Stripe) e sujeitos
            aos termos destes fornecedores.
          </p>
          <p>
            Assinaturas (quando aplicáveis) são cobradas de forma recorrente e podem ser canceladas a
            qualquer momento pelo usuário, com efeito no próximo ciclo de cobrança.
          </p>
        </Section>

        <Section title="6. Responsabilidades">
          <p>
            A RAF Direct empenha-se em selecionar e manter parceiros idôneos, com registros
            profissionais válidos. Contudo, <strong>a responsabilidade técnica pelos serviços clínicos
            é exclusiva das clínicas e profissionais credenciados</strong> que efetivamente prestem o
            atendimento. Reclamações relativas ao serviço clínico devem ser dirigidas ao prestador
            e, quando cabível, aos conselhos profissionais competentes.
          </p>
          <p>
            A RAF Direct não se responsabiliza por falhas em serviços de terceiros (internet,
            operadoras, aplicativos de mensagem), casos fortuitos ou de força maior.
          </p>
        </Section>

        <Section title="7. Propriedade intelectual">
          <p>
            Marca, logotipos, textos, layout, código e demais elementos da plataforma são de
            propriedade da RAF Direct ou licenciados a ela, sendo vedada a reprodução sem autorização
            expressa.
          </p>
        </Section>

        <Section title="8. Comunicações">
          <p>
            Ao se cadastrar, você autoriza o envio de comunicações operacionais por e-mail, WhatsApp
            e SMS. É possível descadastrar-se de mensagens de marketing a qualquer momento, sem
            prejuízo das comunicações essenciais ao serviço.
          </p>
        </Section>

        <Section title="9. Suspensão e encerramento">
          <p>
            A RAF Direct pode suspender ou encerrar contas em caso de descumprimento destes Termos,
            fraude, ordem judicial ou por medida de segurança, com prévio aviso quando cabível.
          </p>
        </Section>

        <Section title="10. Alterações">
          <p>
            Estes Termos podem ser atualizados. A versão vigente estará sempre publicada nesta página.
            O uso continuado após alterações significativas implica aceitação dos novos termos.
          </p>
        </Section>

        <Section title="11. Foro e legislação aplicável">
          <p>
            Estes Termos são regidos pela legislação brasileira. Fica eleito o foro da comarca do
            domicílio do usuário para dirimir eventuais controvérsias, salvo disposição legal em
            contrário.
          </p>
        </Section>

        <div className="mt-12 border-t pt-6 text-sm text-slate-500">
          Contato:
          {" "}<a href="mailto:contato@rafdirect.com.br" className="text-[#0B2B62] underline">contato@rafdirect.com.br</a>
          {" "}· Dados institucionais em
          {" "}<Link to="/compliance" className="text-[#0B2B62] underline">/compliance</Link>.
        </div>
      </div>
    </div>
  );
}
